/**
 * Bell row copy for the live engagement notification feed (ENG-5451 / FE5).
 *
 * ## Where the words come from
 *
 * Two sources, deliberately layered.
 *
 * The **subject** on every notification payload is the headline the email leg
 * already renders, resolved server-side for this recipient's role. It is always
 * present and always correct, so it is the default title. That is also exactly
 * what the ticket asks for as the fallback "where no locked copy exists".
 *
 * A **designed** title from the Designed Dozen copy pack overrides it for the
 * events where the pack's line is strictly better *and* renderable from what we
 * actually have. Those strings live in `../components/NotificationBell/derive.ts`
 * and are reproduced here against the wire shape rather than the prototype's
 * scene shape.
 *
 * ## What we do not have, and why the pack is only partly used
 *
 * The producer's in-app payload carries event facts (hours, periods, notes,
 * thresholds, decisions) and no people. Display names are hydrated separately
 * from the viewer-aware engagement read — which yields the **expert** name and
 * the **project** name, and nothing else: `EngagementParticipant` exposes only
 * `role` and `userId`, so a creator's name and the customer org are not
 * reachable from any read the client is entitled to make.
 *
 * So the pack lines keyed on `creator_name` or `customer_org` (#1 request sent,
 * #3 governance sign-off) fall back to their subject rather than render with a
 * hole in them. They are not deleted — when a later ticket enriches the payload
 * or exposes participant identities, those events light up by adding a case
 * here.
 *
 * ## No money
 *
 * The pack's amount clauses ("Max earnings $X", "NTE total") are absent for the
 * same reason: no routing row computes an amount, so no notification carries
 * one. Amounts belong to the engagement read, which segregates them per viewer
 * (§6.3); rendering `$0` from a missing field would be worse than omitting the
 * clause, so the clause is omitted.
 */
import { formatPeriod } from '../components/EngagementHub/format';

/** Event codes the producer emits (`engagement/service/notifications.go`). */
export const EVENT = {
  requestSent: 'engagement.notify.request_sent',
  offerMade: 'engagement.notify.offer_made',
  offerDeclined: 'engagement.notify.offer_declined',
  requestWithdrawn: 'engagement.notify.request_withdrawn',
  accepted: 'engagement.notify.accepted',
  governancePending: 'engagement.notify.governance_pending',
  governanceDecided: 'engagement.notify.governance_decided',
  hoursSubmitted: 'engagement.notify.hours_submitted',
  hoursConfirmed: 'engagement.notify.hours_confirmed',
  hoursPartiallyConfirmed: 'engagement.notify.hours_partially_confirmed',
  hoursDisputed: 'engagement.notify.hours_disputed',
  hoursResolved: 'engagement.notify.hours_resolved',
  nteThreshold80: 'engagement.notify.nte_threshold_80',
  nteThreshold100: 'engagement.notify.nte_threshold_100',
  amendmentProposed: 'engagement.notify.amendment_proposed',
  amendmentCountered: 'engagement.notify.amendment_countered',
  amendmentWithdrawn: 'engagement.notify.amendment_withdrawn',
  amendmentAccepted: 'engagement.notify.amendment_accepted',
  amendmentPendingGovernance: 'engagement.notify.amendment_pending_governance',
} as const;

/** The payload fields the bell reads. Every one is optional by contract. */
export interface NotificationPayload {
  engagement_id?: string | null;
  hub_tab?: string | null;
  subject?: string | null;
  submission_id?: string | null;
  hours_submitted?: string | null;
  hours_confirmed?: string | null;
  outcome?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  confirm_note?: string | null;
  dispute_note?: string | null;
  resolution?: string | null;
  decline_note?: string | null;
  decision?: string | null;
  governance_note?: string | null;
  threshold_pct?: string | null;
  nte_hours_total?: string | null;
  hours_confirmed_total?: string | null;
}

/** Per-engagement context hydrated from the engagement read. */
export interface NotificationContext {
  /** Short label for the row and the filter pills. */
  label: string;
  expertName?: string;
  projectName?: string;
  /** True when the viewer is the expert on this engagement. */
  viewerIsExpert: boolean;
}

export interface NotificationCopy {
  title: string;
  detail?: string;
  /** A note rendered verbatim, as a blockquote. */
  quote?: string;
}

/** The Hub tabs a deep link may target. */
const HUB_TABS = ['overview', 'hours', 'messages', 'amendments', 'billing'] as const;
export type HubTab = (typeof HUB_TABS)[number];

/**
 * Where a row's deep link goes. The server sends `hub_tab`; this only guards
 * against a value the Hub could not render, which would drop the viewer on a
 * tab that is not in their tab list.
 */
export function hubTabFor(payload: NotificationPayload): HubTab {
  const tab = payload.hub_tab ?? '';
  return (HUB_TABS as readonly string[]).includes(tab) ? (tab as HubTab) : 'overview';
}

/** `/engagements/:id?tab=:tab` — the Hub reads `?tab` on mount already. */
export function notificationHref(payload: NotificationPayload): string | null {
  const id = payload.engagement_id;
  return id ? `/engagements/${encodeURIComponent(id)}?tab=${hubTabFor(payload)}` : null;
}

function period(payload: NotificationPayload): string | null {
  const { period_start: start, period_end: end } = payload;
  return start && end ? formatPeriod(start, end) : null;
}

/** "the Apr 13–17 submission" / "the submission" when the period is unset. */
function submissionPhrase(payload: NotificationPayload): string {
  const p = period(payload);
  return p ? `the ${p} submission` : 'the submission';
}

/** First non-empty note. Only one is ever set per event. */
function noteFor(payload: NotificationPayload): string | undefined {
  return (
    payload.dispute_note ??
    payload.decline_note ??
    payload.governance_note ??
    payload.resolution ??
    payload.confirm_note ??
    undefined
  );
}

/**
 * Resolve a row's copy.
 *
 * `subject` is the title unless a designed line applies. `detail` and `quote`
 * are additive — they never contradict the title, so a fallback title still
 * reads correctly alongside them.
 *
 * `detail` never repeats the engagement name on its own: the row already prints
 * that in its context line, and saying it twice reads as a rendering bug.
 */
export function notificationCopy(
  code: string,
  payload: NotificationPayload,
  ctx: NotificationContext
): NotificationCopy {
  const subject = payload.subject ?? 'Engagement update';
  const expert = ctx.expertName ?? 'The expert';
  const project = ctx.projectName ?? ctx.label;
  const quote = noteFor(payload);

  switch (code) {
    // pack #4 — the pack names the expert; the subject does not.
    case EVENT.offerDeclined:
      return {
        title: `${expert} declined the engagement request`,
        detail: `On ${project}. You can send a new request to find another expert.`,
        quote,
      };

    // §10 #5 — likewise, the subject omits who made the offer.
    case EVENT.offerMade:
      return { title: `${expert} made an offer — review it` };

    // pack #2, customer side only. The expert's pack line needs the customer
    // org, which no client-side read exposes, so the expert keeps the subject.
    case EVENT.accepted:
      return ctx.viewerIsExpert
        ? { title: subject, detail: `On ${project}.` }
        : { title: `Engagement active — ${expert} on ${project}` };

    // §10 #13 — hours and period both come off the payload.
    case EVENT.hoursSubmitted: {
      const hours = payload.hours_submitted;
      const p = period(payload);
      return {
        title: hours ? `${expert} submitted ${hours} hours${p ? ` — ${p}` : ''}` : subject,
      };
    }

    // pack #5. This row routes to the expert only, so "payment on the way" is
    // addressed correctly. The pack's amount clause is dropped — see the header.
    case EVENT.hoursConfirmed: {
      const hours = payload.hours_confirmed;
      return {
        title: hours ? `${hours} hours confirmed — payment on the way` : subject,
        detail: `Your ${submissionPhrase(payload).replace(/^the /, '')} was confirmed.`,
        quote,
      };
    }

    case EVENT.hoursPartiallyConfirmed: {
      const confirmed = payload.hours_confirmed;
      const submitted = payload.hours_submitted;
      return {
        title: subject,
        detail:
          confirmed && submitted
            ? `${confirmed} of ${submitted} hours confirmed on ${submissionPhrase(payload)}.`
            : undefined,
        quote,
      };
    }

    // pack #8. The pack names the disputing approver, which we cannot resolve,
    // so the role-aware subject leads and the payload carries the rest.
    case EVENT.hoursDisputed:
      return {
        title: subject,
        detail: `On ${submissionPhrase(payload)}. Auto-approval is paused while it is open.`,
        quote,
      };

    // pack #9.
    case EVENT.hoursResolved:
      return {
        title: payload.outcome ? `Dispute resolved — ${payload.outcome}` : subject,
        detail: `The dispute on ${submissionPhrase(payload)} was resolved.`,
        quote,
      };

    // pack #6 — names the engagement, which the subject does not.
    case EVENT.nteThreshold80: {
      const { hours_confirmed_total: used, nte_hours_total: cap, threshold_pct: pct } = payload;
      return {
        title: `${project} is approaching its NTE limit`,
        detail:
          used && cap
            ? `Confirmed hours have reached ${used} of ${cap}${pct ? ` (${pct}%)` : ''}.`
            : undefined,
      };
    }

    // pack #7 — LOCKED body line.
    case EVENT.nteThreshold100: {
      const cap = payload.nte_hours_total;
      return {
        title: 'NTE reached — no further hours can be submitted',
        detail: cap
          ? `The engagement has reached its ${cap}-hour cap. Without an amendment, it begins closing in 7 days.`
          : undefined,
      };
    }

    case EVENT.governanceDecided:
      return { title: subject, quote };

    // Everything else — request sent, request withdrawn, governance pending and
    // the four amendment rows — renders its subject. The pack lines for those
    // are keyed on a creator name the client cannot resolve; see the header.
    default:
      return { title: subject, quote };
  }
}
